/**
 * Read-only "drill-in" view of a SUB-WORKFLOW's execution (ADR-0013 addendum).
 *
 * A ``call_workflow`` node runs its child in-process and carries the child's
 * per-node traces in ``log_outputs.sub_traces`` (aggregated on the backend). This
 * modal renders the child workflow's CANVAS read-only + overlays each node's
 * status (green/red/grey ring), n8n-style. Statuses come from BOTH:
 *   - LIVE SSE events → the sub-workflow lights up DURING the run. The child
 *     runs under its OWN execution_id (it has a run row of its own), so its
 *     node events are matched on their ``parent_execution_id`` back-pointer —
 *     a plain ``execution_id`` match finds nothing and the canvas stays grey.
 *   - the persisted ``sub_traces`` (after the run) → the final statuses + the
 *     per-node input/output shown on click.
 * The def (positions + edges) is fetched by ``child_workflow_id``.
 */
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import * as React from "react";
import ReactFlow, {
  Background,
  BackgroundVariant,
  Controls,
  Handle,
  Position,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useStoreApi,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
  type NodeProps,
  type NodeTypes,
  type ReactFlowInstance,
} from "reactflow";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ItemBatchViewer } from "@/components/workflows/ItemBatchViewer";
import { NodeIconBadge } from "@/components/workflows/nodeIcons";
import { isEntrypointKind } from "@/components/workflows/nodeGraph";
import { outputHandlesFor } from "@/components/workflows/outputHandles";
import { useLiveEvents } from "@/lib/eventStream";
import {
  type CountableEvent,
  foldLiveCounts,
  matchesRun,
  stateForRun,
} from "@/lib/workflows/liveCounts";
import {
  workflowsApi,
  type CatalogNode,
  type NodeExecutionSummary,
  type NodeKind,
} from "@/lib/api-workflows";
import { cn } from "@/lib/cn";

type SubTrace = {
  node_id?: string;
  node_kind?: string;
  status?: string;
  duration_ms?: number;
  /** TRUE emitted count, read backend-side BEFORE the row cap. `output` is a
   *  capped SAMPLE — never count it (see the `itemCount` memo). Optional: a
   *  trace persisted before this field existed won't carry it. */
  items_out?: number;
  input?: unknown;
  output?: unknown;
  error?: string | null;
};

/** The same status-ring vocabulary the editor's TrayNodeBox uses, so the
 *  sub-workflow canvas reads identically to the main canvas. */
function statusRingClass(status: string | undefined): string {
  if (status === "failed")
    return " ring-2 ring-danger shadow-[0_0_18px_rgba(239,68,68,0.65)] bg-danger/10";
  if (status === "running") return " tray-node-running bg-amber-400/5";
  if (status === "skipped") return " opacity-50";
  if (status === "succeeded") return " ring-2 ring-emerald-500 bg-emerald-500/5";
  return "";
}

type SubWfNodeData = {
  kind: NodeKind;
  label: string;
  category: string | undefined;
  status: string | undefined;
  isEntry: boolean;
  /** Named output handles (IF/SWITCH/loop/sink) so edges carrying a
   *  ``source_handle`` connect to the RIGHT dot — exactly like the parent
   *  canvas. Empty ⇒ one unnamed default source handle. */
  handles: { id: string; label: string }[];
};

/** A read-only clone of the editor's node box (same icon badge, uppercase kind
 *  label, name, status ring, AND the same named output handles) — so the
 *  sub-workflow canvas matches the real editor canvas 1:1, and every edge
 *  (incl. IF/SWITCH/loop/sink branches carrying a ``source_handle``) actually
 *  connects instead of being silently dropped by ReactFlow. */
function SubWfNode({ data }: NodeProps<SubWfNodeData>) {
  const named = data.handles;
  return (
    <div
      className={
        "relative min-w-[180px] border border-border bg-elevated px-3 py-2 text-xs text-foreground shadow-sm" +
        statusRingClass(data.status)
      }
    >
      {!data.isEntry && (
        <Handle type="target" position={Position.Left} className="!h-2 !w-2 !bg-foreground" />
      )}
      <div className="flex items-center gap-2">
        <NodeIconBadge kind={data.kind} category={data.category} size={28} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[10px] uppercase tracking-wider text-muted-foreground">
            {data.kind}
          </div>
          <div className="truncate font-medium text-foreground">{data.label}</div>
        </div>
      </div>
      {named.length > 0 ? (
        // Named output handles as vertical labelled rows on the right —
        // mirrors TrayNodeBox so an IF/SWITCH/loop/sink edge lands on the
        // handle whose id === edge.source_handle.
        <div className="mt-2 flex flex-col">
          {named.map((h) => (
            <div
              key={h.id}
              className="relative flex h-[22px] items-center justify-end gap-1 pr-3 text-[10px] text-muted-foreground"
            >
              <span className="font-mono">{h.label || h.id}</span>
              <Handle
                type="source"
                position={Position.Right}
                id={h.id}
                className="!h-2 !w-2 !bg-foreground"
                style={{ right: -4 }}
              />
            </div>
          ))}
        </div>
      ) : (
        <Handle type="source" position={Position.Right} className="!h-2 !w-2 !bg-foreground" />
      )}
    </div>
  );
}

const NODE_TYPES: NodeTypes = { tray: SubWfNode };

/** The ReactFlow canvas, rendered INSIDE `ReactFlowProvider` so it can reach the
 *  internal store via `useStoreApi`. This is the fix for "edges invisible in the
 *  drill-in": when this ReactFlow mounts inside a just-opened dialog, its per-node
 *  ResizeObserver fires once while the node box is still 0-sized, records
 *  `width/height = 0`, and — because each node's OWN box never changes afterwards
 *  (only the dialog ancestor settles) — never fires again. So the store keeps
 *  `width = null` and never computes `handleBounds`; without handle bounds
 *  ReactFlow cannot resolve edge endpoints and silently DROPS every edge (they
 *  never reach the DOM even though the `edges` prop is correct). `useUpdateNodeInternals`
 *  proved unreliable here (it queued but didn't re-measure), so we replicate the
 *  store's own measurement directly: read each rendered `.react-flow__node` element
 *  and call `updateNodeDimensions([...], forceUpdate)`. We retry on a short schedule
 *  because the exact frame the nodes get real size varies with the dialog paint. */
function SubWfCanvas({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  rfRef,
  onNodeClick,
}: {
  nodes: Node<SubWfNodeData>[];
  edges: Edge[];
  onNodesChange: (changes: NodeChange[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  rfRef: React.MutableRefObject<ReactFlowInstance | null>;
  onNodeClick: (id: string) => void;
}) {
  const storeApi = useStoreApi();
  // Re-measure keyed on `nodes` (not just the id set): every time the controlled
  // `nodes` prop gets a new identity — which happens on each SSE-driven recompute,
  // even when the node SET is unchanged — ReactFlow resets its internal node
  // dimensions and drops handleBounds, so we must re-measure or the edges vanish.
  React.useEffect(() => {
    if (nodes.length === 0) return;
    let cancelled = false;
    const measure = () => {
      if (cancelled) return;
      const state = storeApi.getState();
      const dom = state.domNode;
      if (!dom) return;
      const updates: {
        id: string;
        nodeElement: HTMLDivElement;
        forceUpdate: boolean;
      }[] = [];
      dom.querySelectorAll<HTMLDivElement>(".react-flow__node").forEach((el) => {
        const id = el.getAttribute("data-id");
        if (id) updates.push({ id, nodeElement: el, forceUpdate: true });
      });
      if (updates.length) state.updateNodeDimensions(updates);
      rfRef.current?.fitView({ padding: 0.2 });
    };
    const raf = requestAnimationFrame(measure);
    // Retry: the frame the nodes acquire real size varies with the dialog paint.
    const timers = [80, 220, 450, 800].map((d) => window.setTimeout(measure, d));
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [nodes, storeApi, rfRef]);
  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      nodeTypes={NODE_TYPES}
      onInit={(inst) => {
        rfRef.current = inst;
        requestAnimationFrame(() => inst.fitView({ padding: 0.2 }));
      }}
      fitView
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable
      proOptions={{ hideAttribution: true }}
      onNodeClick={(_e, n) => onNodeClick(n.id)}
    >
      <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
      <Controls position="bottom-left" showInteractive={false} />
    </ReactFlow>
  );
}

/** Build the dialog props from a call_workflow node's persisted trace (logs
 *  panel entry — carries the child id + name + per-node payloads). */
export function subWfPropsFromNode(node: NodeExecutionSummary): {
  childWorkflowId: string;
  childName: string;
  subTraces: SubTrace[];
} {
  const lo = (node.log_outputs ?? {}) as Record<string, unknown>;
  return {
    childWorkflowId: String(lo.child_workflow_id ?? ""),
    childName: String(lo.sub_workflow_name ?? ""),
    subTraces: (Array.isArray(lo.sub_traces) ? lo.sub_traces : []) as SubTrace[],
  };
}

export function SubWorkflowRunDialog({
  childWorkflowId,
  childName,
  subTraces = [],
  executionId,
  onClose,
}: {
  childWorkflowId: string;
  childName?: string;
  /** Persisted per-node traces (logs entry). Absent on a live canvas entry —
   *  statuses then come purely from live SSE + payloads are unavailable. */
  subTraces?: SubTrace[];
  /** The execution to overlay LIVE (child SSE events ride the parent's id). */
  executionId?: string | null;
  onClose: () => void;
}) {
  const childId = childWorkflowId;

  // Final status + payloads keyed by the child node id (post-run).
  const byNodeId = React.useMemo(() => {
    const m = new Map<string, SubTrace>();
    for (const t of subTraces) if (t.node_id) m.set(t.node_id, t);
    return m;
  }, [subTraces]);

  // LIVE status overlay: the child's nested executor emits node_started/finished
  // on the PARENT execution_id, so we scan the live buffer for matching events.
  const { events, generation } = useLiveEvents();
  // Keep the PREVIOUS map so an event that changes nothing here returns the
  // SAME reference — otherwise `liveStatus` is a fresh Map on EVERY global SSE
  // event (`generation` bumps app-wide), `statusOf`/`nodes`/`edges` all
  // recompute, and ReactFlow re-renders the whole canvas ~continuously during a
  // busy run → heavy flicker while zooming/panning. A stable reference lets the
  // downstream memos bail out when the statuses didn't actually change.
  // Per-node LIVE item counts for the edge pills — the sub-workflow twin of the
  // editor's. ACCUMULATED (never re-summed from the buffer: it's a capped ring,
  // so a long run evicts its own early events and the counts would vanish), and
  // keyed by `workflow_id:node_id` because the parent, this child and its
  // SIBLING sub-workflows share ONE execution_id with colliding node ids.
  //
  // The accumulator lives at MODULE scope keyed by run (`stateForRun`), NOT in a
  // ref: a ref dies with the dialog, and the events it had folded are already
  // gone from the 200-event ring — so closing the drill-in and reopening it
  // mid-run used to restart the count from the few events still buffered and
  // come back LOWER. Keyed by run, a reopen RESUMES and a re-run starts clean.
  const runKey = executionId ? `${executionId}:${childId}` : null;
  const [liveCounts, setLiveCounts] = React.useState<Record<string, number>>({});

  // Seed from the run's accumulator on mount / run change — a reopen must show
  // what was already counted, not wait for the next event (which may never come
  // if the run already finished).
  React.useEffect(() => {
    if (!runKey) return;
    setLiveCounts({ ...stateForRun(runKey).counts });
  }, [runKey]);

  React.useEffect(() => {
    if (!executionId || !runKey) return;
    const state = stateForRun(runKey);
    const changed = foldLiveCounts(events as unknown as CountableEvent[], state, {
      executionId,
      // The child runs under its OWN execution now, and its node events carry
      // the parent as `parent_execution_id` — opt in, or the edge pills never
      // count anything. `keyOf` still narrows to THIS child's workflow.
      includeSubRuns: true,
      keyOf: (e) => (e.workflow_id === childId && e.node_id ? e.node_id : null),
    });
    // Only publish on a real change — a new object identity every `generation`
    // would rebuild nodes/edges and re-render-storm the ReactFlow canvas.
    if (changed) setLiveCounts({ ...state.counts });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generation, executionId, childId, runKey]);

  // Item count per node driving the edge pills: the LIVE accumulator, overlaid
  // by the persisted trace's count once the run is over (exact).
  //
  // Read `items_out` — the trace's OWN integer — never `output.length`. The
  // backend row-caps `output` (20) and `_bound_sub_traces` empties it entirely
  // when the blob is over budget, so counting the array under-reported a big
  // fan-out AND made the pill DROP when the trace landed (100 → 33). The count
  // is metadata; `output` is only a sample. Fall back to the array for traces
  // persisted before `items_out` existed.
  const itemCount = React.useMemo<Record<string, number>>(() => {
    const m: Record<string, number> = { ...liveCounts };
    for (const t of subTraces) {
      if (!t.node_id) continue;
      if (typeof t.items_out === "number") m[t.node_id] = t.items_out;
      else if (Array.isArray(t.output)) m[t.node_id] = t.output.length;
    }
    return m;
  }, [liveCounts, subTraces]);

  const liveStatusRef = React.useRef<Map<string, string>>(new Map());
  const liveStatus = React.useMemo(() => {
    const m = new Map<string, string>();
    if (!executionId) {
      if (liveStatusRef.current.size === 0) return liveStatusRef.current;
      liveStatusRef.current = m;
      return m;
    }
    // Two things bite here (both caused "every node stuck on running"):
    //  1. The buffer is NEWEST-FIRST, so we must keep each node's FIRST-seen
    //     (newest) status and IGNORE older ones — otherwise last-write-wins
    //     lands on the OLDEST event (node_started) and every node reads
    //     "running" forever.
    //  2. The parent and its SIBLING sub-workflows have auto-generated node ids
    //     that COLLIDE (`http_request_1` exists in each). So we MUST disambiguate
    //     by the workflow the node belongs to (`workflow_id`), else a
    //     sibling/parent node's status bleeds onto this canvas.
    //
    // The child runs under its OWN execution_id (it has a run row of its own),
    // so matching `execution_id === executionId` alone would find NOTHING and
    // the canvas would stay grey for the whole run — the child's events carry
    // the parent as `parent_execution_id`, so accept either.
    const seen = new Set<string>();
    for (const e of events) {
      if (e.kind !== "node_started" && e.kind !== "node_finished") continue;
      if (!matchesRun(e as CountableEvent, executionId, true)) continue;
      if (childId && e.workflow_id !== childId) continue;
      const nid = e.node_id as string | undefined;
      const st = e.status as string | undefined;
      if (!nid || !st || seen.has(nid)) continue;
      seen.add(nid);
      m.set(nid, st);
    }
    // Return the SAME reference when the contents didn't change (see above) so
    // the downstream `nodes`/`edges` memos don't rebuild every SSE event.
    const prev = liveStatusRef.current;
    if (prev.size === m.size) {
      let identical = true;
      for (const [k, v] of m) {
        if (prev.get(k) !== v) {
          identical = false;
          break;
        }
      }
      if (identical) return prev;
    }
    liveStatusRef.current = m;
    return m;
    // generation bumps on every new event → recompute.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionId, childId, generation]);

  // A node's effective status: LIVE wins while a run is in flight, else the
  // persisted trace.
  const statusOf = React.useCallback(
    (nid: string): string | undefined => liveStatus.get(nid) ?? byNodeId.get(nid)?.status,
    [liveStatus, byNodeId],
  );

  const defQuery = useQuery({
    queryKey: ["workflow-detail", childId],
    queryFn: () => workflowsApi.get(childId),
    enabled: !!childId,
  });
  // The node catalog gives each kind its label + category (→ icon tint), so the
  // sub-workflow nodes render EXACTLY like the editor canvas.
  const catalogQuery = useQuery({
    queryKey: ["workflow-catalog"],
    queryFn: () => workflowsApi.catalog(),
    staleTime: 5 * 60 * 1000,
  });
  const specByKind = React.useMemo(() => {
    const m = new Map<string, CatalogNode>();
    for (const n of catalogQuery.data?.nodes ?? []) m.set(n.kind, n);
    return m;
  }, [catalogQuery.data]);

  const [selected, setSelected] = React.useState<string | null>(null);
  const [tab, setTab] = React.useState<"input" | "output">("output");
  const rfRef = React.useRef<ReactFlowInstance | null>(null);

  const def = defQuery.data?.active_version?.definition;

  const computedNodes: Node<SubWfNodeData>[] = React.useMemo(() => {
    if (!def) return [];
    return def.nodes.map((n) => {
      const spec = specByKind.get(n.kind);
      return {
        id: n.id,
        position: n.position ?? { x: 0, y: 0 },
        type: "tray",
        selectable: true,
        draggable: false,
        data: {
          kind: n.kind,
          label: n.label || spec?.label || n.kind,
          category: spec?.category,
          status: statusOf(n.id),
          isEntry: isEntrypointKind(n.kind, spec?.category),
          handles: outputHandlesFor(n.kind, n.config ?? {}),
        },
      };
    });
  }, [def, specByKind, statusOf]);

  const computedEdges: Edge[] = React.useMemo(() => {
    if (!def) return [];
    return def.edges.map((e) => {
      // A traversed edge (source finished AND target was reached) paints
      // green — same run visualization as the parent canvas. Others stay a
      // neutral gray so the green path traces the real flow. NB: CONCRETE
      // colors, NOT a CSS var — the var is `--muted-fg` (there is NO
      // `--muted-foreground`), so `hsl(var(--muted-foreground))` was an
      // INVALID color → the path stroke didn't render (invisible edges) and
      // the marker fell back to BLACK. No `markerEnd` arrow either — the parent
      // canvas draws plain beziers with no arrowheads.
      const src = statusOf(e.source);
      const tgt = statusOf(e.target);
      const traversed =
        src === "succeeded" && (tgt === "succeeded" || tgt === "running" || tgt === "failed");
      return {
        id: e.id,
        source: e.source,
        target: e.target,
        // `|| undefined` (not `?? undefined`): a stored EMPTY-STRING
        // source_handle must fall back to the node's default (unnamed) handle,
        // else ReactFlow can't match it → the edge is silently dropped
        // (invisible). Named branches (if/switch/loop/sink) keep their id.
        sourceHandle: e.source_handle || undefined,
        animated: !!executionId && traversed && liveStatus.size > 0,
        // How many items crossed this edge — the same read the main canvas
        // gives, and the whole point of watching a sub-workflow run.
        label: itemCount[e.source] != null ? `${itemCount[e.source]} item` : undefined,
        labelShowBg: true,
        labelBgPadding: [4, 2] as [number, number],
        labelBgBorderRadius: 4,
        labelStyle: { fill: "#94a3b8", fontSize: 10 },
        labelBgStyle: { fill: "#0b0b0b" },
        style: {
          stroke: traversed ? "#10b981" : "#64748b",
          strokeWidth: traversed ? 2.5 : 1.5,
        },
      };
    });
  }, [def, executionId, statusOf, liveStatus, itemCount]);

  // CONTROLLED node/edge state with `onNodesChange` wired — LOAD-BEARING for the
  // "invisible edges in the drill-in" bug. `onNodesChange` is how the measured
  // `dimensions` (width/height + handleBounds) get written back onto each node
  // object; ReactFlow renders an edge only when BOTH endpoints have `width`,
  // `height` and `handleBounds` (see `getNodeData`). We keep the computed
  // nodes/edges as the source of truth and MERGE them in (see the effect below).
  const [rfNodes, setRfNodes, onNodesChange] = useNodesState<SubWfNodeData>([]);
  const [rfEdges, setRfEdges, onEdgesChange] = useEdgesState([]);
  React.useEffect(() => {
    // MERGE, don't replace: preserve the measured `width`/`height` already
    // stamped onto each node (see SubWfCanvas). ReactFlow's `createNodeInternals`
    // rebuilds each internal node as `{...incomingNode}` on every `nodes` prop
    // change, so any measured dimension NOT carried on the node object is lost —
    // and `getNodeData` drops an edge whose endpoints lack `width`/`height`.
    // Normally RF's ResizeObserver re-measures immediately, but inside a modal it
    // fires once (at 0×0) and never again, so we must carry the dims ourselves.
    setRfNodes((prev) => {
      const dims = new Map(prev.map((n) => [n.id, n]));
      return computedNodes.map((n) => {
        const old = dims.get(n.id);
        return old?.width ? { ...n, width: old.width, height: old.height } : n;
      });
    });
  }, [computedNodes, setRfNodes]);
  React.useEffect(() => {
    setRfEdges(computedEdges);
  }, [computedEdges, setRfEdges]);

  // Fit the graph once it's laid out. A ReactFlow mounted inside a just-opened
  // dialog measures 0×0 during the enter animation, so the ``fitView`` prop runs
  // against an empty viewport and the nodes end up off-screen — re-fit on the
  // next frame once the container has real size.
  React.useEffect(() => {
    if (!def || computedNodes.length === 0) return;
    const id = requestAnimationFrame(() => rfRef.current?.fitView({ padding: 0.2, duration: 200 }));
    return () => cancelAnimationFrame(id);
  }, [def, computedNodes.length]);

  const selectedTrace = selected ? byNodeId.get(selected) : undefined;
  const payload = selectedTrace
    ? tab === "input"
      ? selectedTrace.input
      : selectedTrace.output
    : undefined;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="!w-[94vw] !max-w-[1600px] sm:!h-[86vh] flex flex-col p-0">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">
            Esecuzione sub-workflow ·{" "}
            <span className="text-muted-foreground">
              {childName || def?.name || "sub-workflow"}
            </span>
          </DialogTitle>
        </DialogHeader>
        <div className="flex min-h-0 flex-1">
          {/* Canvas (read-only) with per-node status overlay. */}
          <div className="relative min-w-0 flex-1 border-r border-border/60">
            {defQuery.isLoading ? (
              <div className="flex h-full items-center justify-center text-[12px] text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
                Carico il sub-workflow…
              </div>
            ) : !def ? (
              <div className="flex h-full items-center justify-center px-6 text-center text-[12px] text-muted-foreground">
                Definizione del sub-workflow non disponibile
                {defQuery.isError ? " (errore di caricamento)." : "."}
              </div>
            ) : (
              <ReactFlowProvider>
                <SubWfCanvas
                  nodes={rfNodes}
                  edges={rfEdges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  rfRef={rfRef}
                  onNodeClick={setSelected}
                />
              </ReactFlowProvider>
            )}
          </div>

          {/* Selected-node detail. */}
          <div className="flex w-80 shrink-0 flex-col">
            {selectedTrace ? (
              <>
                <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border/60 px-3 py-1.5">
                  <div className="flex min-w-0 items-center gap-2 text-[12px]">
                    <span className="truncate font-medium text-foreground">{selected}</span>
                    <span
                      className={cn(
                        "text-[11px] uppercase",
                        selectedTrace.status === "failed"
                          ? "text-destructive"
                          : selectedTrace.status === "skipped"
                            ? "text-muted-foreground"
                            : "text-success",
                      )}
                    >
                      {selectedTrace.status}
                    </span>
                  </div>
                  <div className="inline-flex shrink-0 rounded-md border border-border bg-background p-0.5">
                    {(["input", "output"] as const).map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setTab(t)}
                        className={cn(
                          "rounded px-2 py-0.5 text-[10px] font-medium uppercase",
                          tab === t
                            ? "bg-foreground text-background"
                            : "text-muted-foreground hover:text-foreground",
                        )}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
                {selectedTrace.error && (
                  <div className="border-b border-border/60 bg-destructive/10 px-3 py-1.5 font-mono text-[11px] text-destructive">
                    {selectedTrace.error}
                  </div>
                )}
                <div className="flex-1 overflow-auto p-3">
                  {payload === undefined || payload === null ? (
                    <div className="text-[11px] text-muted-foreground">
                      ({tab} non catturato per questo nodo)
                    </div>
                  ) : (
                    <ItemBatchViewer payload={payload} />
                  )}
                </div>
              </>
            ) : (
              <div className="m-3 text-[11px] text-muted-foreground">
                Clicca un nodo del sub-workflow per vederne input/output.
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
